import { computed, ref } from "@vue/composition-api";
import {
  child_to_parent_tree_injection,
  clone,
  delayBy,
  flat_by,
  groupBy,
  identity,
  indexBy,
  isNotEmpty,
  memoize,
  strSearch,
  uniq,
  uniqby,
} from "@/fp";

import { SharedState } from ".";
import {
  get_adhoc_labels,
  get_context_labels,
  get_abstract_has_mip_and_PII_labels,
  get_dynamics_labels,
  get_content_labels_tree,
  getAllVirtualLabels,
  getL1sDesc,
  get_internal_external_abstract_labels,
  get_folders_context_labels,
} from "../api/labels.api";
import { objectsSummary } from "../api/summary.api";
import {
  FoldersContextLabel,
  InformationType,
  InformationTypeTree,
  POPULATION_TYPE,
  SensitivityLevelHelper,
} from "../models";
import { state } from "./state";
import isEmpty from "lodash.isempty";
import { restriction_to_info_type_code } from "../models/admin-restrictions.model";
import { org_settings } from "../api/user-settings.api";

const DeletedCustomLabelsSettings = org_settings<string[]>(
  "deleted_custom_labels",
  []
);

const { action, getter, m, mutation } = state;

export const customInfoTypes = getter((s) => s.customInfoTypes);

export const info_types_desc = getter((s) => s.info_types_desc);
const set_info_types_desc = m.set("info_types_desc");

export const info_types_tree = getter((s) => s.info_types_tree);
let extensible_info_types_tree: InformationTypeTree[] = [];

const set_info_types_tree = mutation<InformationTypeTree[]>(
  "set info type tree",
  (s, info_type_tree) => {
    if (SharedState.data_sources_contain_only_gmail.value) {
      const personal = info_type_tree.find((f) => f.code === 7);
      if (personal) {
        personal.nested = personal.nested.filter((f) => f.id != "ms");
      }
    }

    if (SharedState.restrictions.user_restrictions.value.info.length > 0) {
      SharedState.restrictions.user_restrictions.value.info.forEach(
        (info_code) => {
          const l = info_type_tree.find((f) => f.code === info_code);
          if (l) {
            // @ts-ignore
            l.is_restricted = true;
          }
        }
      );
    }
    extensible_info_types_tree = clone(info_type_tree, true);
    return {
      ...s,
      info_types_tree: info_type_tree,
    };
  }
);

export const update_info_type_in_tree = (info_type: InformationTypeTree) => {
  // @ts-ignore
  const { path, content_path = [] } = info_type;

  // @ts-ignore
  if (path.length > 0) {
    const tree = [...info_types_tree.value];
    let current = tree;
    // @ts-ignore
    path.forEach((section_id: string) => {
      // @ts-ignore
      if (current.nested) {
        // @ts-ignore
        current = current.nested.find((f) => f.id == section_id);
      } else {
        // @ts-ignore
        current = current.find((f) => f.id == section_id);
      }
    });
    // @ts-ignore
    Object.assign(current, info_type);

    if (content_path.length > 0) {
      let current_2 = tree;
      // @ts-ignore
      content_path.forEach((section_id: string) => {
        // @ts-ignore
        if (current_2.nested) {
          // @ts-ignore
          current_2 = current_2.nested.find((f) => f.id == section_id);
        } else {
          // @ts-ignore
          current_2 = current_2.find((f) => f.id == section_id);
        }
      });
      // @ts-ignore
      Object.assign(current_2, info_type);
    }
    set_info_types_tree(tree);
  }
};

export const get_raw_info_types_tree = (
  L0_L1 = ["", ""],
  include = false
): InformationTypeTree[] => {
  const [l0_value, l1_value] = L0_L1;
  const [L0, L1] = L0_L1.map((label) => [isEmpty(label), isNotEmpty(label)]);
  const [L0_EMPTY, L0_FULL] = L0;
  const [L1_EMPTY, L1_FULL] = L1;
  const full_tree = clone(extensible_info_types_tree, true);

  switch (true) {
    case L0_FULL && L1_FULL:
      return full_tree
        .filter((l) => l.id === l0_value)[0]
        .nested.filter((l1) => l1.id === l1_value);
    case L0_FULL && L1_EMPTY:
      return full_tree.filter((l) => l.id === l0_value)[0].nested;
    case L0_EMPTY && L1_FULL:
      throw new Error("get_raw_info_types_tree can't have L0_EMPTY && L1_FULL");
    case L0_EMPTY && L1_EMPTY:
    default:
      return full_tree;
  }
};

export const allInfoTypes = computed(() => {
  const flat_tree_labels = flat_info_types_tree(
    clone([...info_types_tree.value], true)
  );

  return indexBy<InformationType>("id")(
    // @ts-ignore
    [
      ...flat_tree_labels,
      // @ts-ignore
      ...internal_external_abstract_labels.value,
      // ...PII_labels.value,
    ]
  );
});

export const l0InfoTypes = computed(() =>
  Object.values(allInfoTypes.value)
    .filter((l) => l.generation === 0)
    .sort((a, b) => (a.l0Order || 0) - (b.l0Order || 0))
);

export const l0_known_content_info_types = computed(() =>
  l0InfoTypes.value.filter((l) => l.id != default_label_id.value)
);

export const l0InfoTypes_index_by_code = computed(() =>
  l0InfoTypes.value.reduce((acc, l0) => {
    acc[l0.code] = l0;
    return acc;
  }, {} as { [key: number]: InformationType })
);

export const non_regulated_l0InfoTypes = computed(() =>
  Object.values(allInfoTypes.value).filter(
    (l) => l.generation === 0 && l.code != 7 && l.labelCategory == "Content"
  )
);

export const infoTypeByName = computed(() =>
  indexBy<InformationType>("labelName")(Object.values(allInfoTypes.value))
);

export const infoTypeByShortName = computed(() =>
  indexBy<InformationType>("shortName")(Object.values(allInfoTypes.value))
);

const types_by_categories = ref(new Map());
const set_types_by_categories = (
  flattened_tree: InformationType[],
  regulated_type_id: string,
  regulated_sub_types: InformationType[]
) => {
  let regulated_types = regulated_sub_types;
  if (!Array.isArray(regulated_sub_types)) {
    regulated_types = Object.values(regulated_sub_types);
  }
  const typesByParentId = groupBy<InformationType>(
    "parentId",
    flattened_tree.filter((l) => l.generation < 2)
  );
  l0InfoTypes.value.forEach((f) => {
    if (!typesByParentId.has(f.id)) {
      typesByParentId.set(f.id, []);
    }
  });
  typesByParentId.set(regulated_type_id, regulated_types);
  types_by_categories.value = typesByParentId;
};
export const typesByCategories = computed(() => types_by_categories.value);

export const regulated_info_types_by_categories = computed(() => {
  const types = typesByCategories.value.get(
    ABSTRACT_REGULATED_L0.id
  ) as InformationType[];
  if (types) {
    // @ts-ignore
    const types_by_code: Map<number, InformationType[]> = groupBy<
      InformationType
    >("content_code", types);
    return Array.from(types_by_code.entries()).reduce(
      (acc, [code, info_types]) => {
        acc[code] = info_types;
        return acc;
      },
      {} as Record<number, InformationType[]>
    );
  } else {
    return {};
  }
});

export const get_l1s = (l0_id: string): InformationType[] => {
  if (typesByCategories.value.has(l0_id)) {
    return typesByCategories.value.get(l0_id) as InformationType[];
  }
  return [];
};

export const abstract_has_mip_labels = getter((s) => s.abstract_has_mip_labels);
const set_abstract_has_mip_labels = m.set("abstract_has_mip_labels");

export const PII_labels = getter((s) => s.PII_labels);
const set_PII_labels = m.set("PII_labels");

export const dynamics_labels = getter((s) => s.dynamics_labels);
const set_dynamics_labels = m.set("dynamics_labels");

export const virtualLabels = getter((s) => s.virtualLabels);

export const indexed_virtualLabels = getter((s) =>
  indexBy<InformationType>("id")(s.virtualLabels)
);

export const get_l0_by_code = (code: number) =>
  l0InfoTypes.value.find((l) => l.code === code);

export const get_l0_by_id = (id: string) => {
  const info_type = getInfoType(id);
  if (info_type) {
    return l0InfoTypes.value.find((l) => l.code === info_type.code);
  } else {
    return null;
  }
};

export const criticalLabels = getter((s) =>
  s.virtualLabels.filter(
    (l) =>
      l.sensitivity > 0.7 &&
      l.labelCategory !== "hasMipVirtual" &&
      l.abstracts &&
      l.abstracts.length > 0
  )
);

export const mip_labels = getter((s) =>
  s.virtualLabels.filter((l) => l.labelCategory === "hasMipVirtual")
);

export const mip_critical_labels = getter((s) =>
  s.virtualLabels.filter(
    (l) => l.sensitivity > 0.7 && l.labelCategory === "hasMipVirtual"
  )
);

export const mediumSensitivityVirtualLabels = getter((s) =>
  // @ts-ignore
  s.virtualLabels.filter((l) => l.sensitivityLevel === "Medium")
);

export const typesWithoutCritical = computed(() => {
  const l0Ids = l0InfoTypes.value.map((l) => l.id);
  const parents_of_L1_with_level3_sensitivity = Array.from(
    new Set(
      Object.values(allInfoTypes.value)
        .filter((l) => l.sensitivityLevel === 3)
        .map((l) => l.parentId)
    )
  );
  return l0Ids.filter(
    (id) => !parents_of_L1_with_level3_sensitivity.includes(id)
  );
});

export const lowSensitivityVirtualLabels = getter((s) =>
  // @ts-ignore
  s.virtualLabels.filter((l) => l.sensitivityLevel === "Low")
);
const _setVirtualLabels = m.set("virtualLabels");
const _loadVirtualLabels = action<void>(
  "load virtual labels",
  async ({ mutate }) => {
    const res = await getAllVirtualLabels();
    if (res.success) {
      mutate(
        _setVirtualLabels,
        res.value.map((l) => {
          return {
            ...l,
            code: l.code === 0 ? 7 : l.code,
            parentId: l.infoTypeCategoryId,
            sensitivity_int: SensitivityLevelHelper.floatToInt(l.sensitivity),
            name: l.labelName
              .replace("Low+", "Low/Medium/High")
              .replace("Medium+", "Medium/High"),
          };
        })
      );
    }
  }
);

export const context_labels = getter((s) => s.contextLabels);
const set_context_labels = m.set("contextLabels");
export const is_context_label = memoize((id: string) =>
  isNotEmpty(
    [...context_labels.value, ...folders_context_labels.value].find(
      (f) => f.id === id
    )
  )
);

export const those_arr_contains_different_label_types = (ids: string[]) => {
  const label_types = ids
    .filter((id) => getInfoType(id))
    .map((id) => getInfoType(id).labelType);
  return uniq<string>(label_types).length > 1;
};

export const folders_context_labels = getter((s) => s.foldersContextLabels);
export const folders_context_labels_categories = computed(() =>
  Array.from(
    new Set(folders_context_labels.value.map((d) => d.labelCategory as string))
  )
);
// @ts-ignore
window.folders_context_labels_categories = folders_context_labels_categories;
export const update_folders_context_labels = m.updateManyBy<
  FoldersContextLabel
>("foldersContextLabels", "id");
export const add_a_folder_context_label = m.append("foldersContextLabels");
const set_folders_context_labels = m.set("foldersContextLabels");

export const internal_external_abstract_labels = getter(
  (s) => s.internal_external_abstract_labels
);
let indexed_int_ext_labels: Record<string, InformationType> = {};
export const get_internal_external_label_by_id = (id: string) => {
  return indexed_int_ext_labels[id] as InformationType;
};
const set_internal_external_abstract_labels = m.set(
  "internal_external_abstract_labels"
);
const load_internal_external_abstract_labels = action<void>(
  "load virtual labels",
  async ({ mutate }) => {
    const res = await get_internal_external_abstract_labels();
    if (res.success) {
      const int_ext_labels = res.value.map((l) => {
        return {
          ...l,
          parentId: l.infoTypeCategoryId,
        };
      });
      indexed_int_ext_labels = int_ext_labels.reduce((acc, l) => {
        acc[l.id] = l;
        return acc;
      }, {} as Record<string, InformationType>);
      mutate(set_internal_external_abstract_labels, int_ext_labels);
    }
  }
);

export const get_virtual_labels_for_l0_id = (
  categoryId: string
): Array<{ id: string; name: string }> => {
  return virtualLabels.value
    .filter((l) => {
      // @ts-ignore
      return l.infoTypeCategoryId === categoryId;
    })
    .sort((a, b) => a.sensitivity * 10 - b.sensitivity * 10)
    .map((l) => {
      return {
        id: l.id,
        name: l.name || l.labelName,
      };
    });
};

export const getVirtualLabelByLabelCategory = (
  categoryId: string,
  level: "None" | "Low" | "Medium" | "High"
): { id: string; name: string } => {
  const label = virtualLabels.value.find((l) => {
    // @ts-ignore
    return l.infoTypeCategoryId === categoryId && l.sensitivityLevel === level;
  }) as InformationType;

  return {
    id: label.id,
    name: label.labelName,
  };
};

export const getVirtualLabelByContentLabelName = (
  shortName: string,
  level: "None" | "Low" | "Medium" | "High"
): { id: string; name: string } => {
  const lowerCaseShortName = shortName.toLowerCase();
  const search = strSearch(lowerCaseShortName, false);
  const label = virtualLabels.value.find((l) => {
    const is_in_labels_name = search(l.labelName);
    // @ts-ignore
    return is_in_labels_name && l.sensitivityLevel === level;
  }) as InformationType;

  return {
    id: label.id,
    name: label.labelName,
  };
};

export const getVirtualLabelCategoryName = (labelCategoryId: string) => {
  return SharedState.allInfoTypes.value[labelCategoryId].shortName;
};

export const typesForInfoTypesList = computed(() => {
  const allLabels: InformationType[] = flat_info_types_tree(
    clone([...info_types_tree.value], true)
  );
  return allLabels.map((label) => {
    return {
      id: label.id,
      name: label.labelName || label.name || label.shortName,
      // @ts-ignore
      isParent: label.has_children,
      indent: label.generation,
      checked: false,
    };
  });
});

export const getLabelDescription = (labelId: string): string => {
  if (!labelId) {
    return "";
  }
  const l = allInfoTypes.value[labelId] || customInfoTypes.value[labelId];
  return l ? l.desc || l.description || "No description" : "No description";
};

export const infoTypeUtils = {
  isInfoTypeChildOrTheParent(parentIds: string[], childId: string): boolean {
    if (parentIds.includes(childId)) {
      return true;
    }
    const isChild =
      isNotEmpty(allInfoTypes.value[childId]) ||
      isNotEmpty(customInfoTypes.value[childId]);

    return isChild;
  },
  has_children: (id: string) => {
    const { generation, parentId } = allInfoTypes.value[id];
    if (generation === 0) return true;
    if (generation === 2) return false;
    if (generation === 1) {
      const l0 = info_types_tree.value.find(
        (l) => l.id === parentId
      ) as InformationTypeTree;
      const l1 = l0.nested.find((l) => l.id === id);
      if (l1 && l1.nested) {
        return l1.nested.length > 0;
      }
      return false;
    }
    return false;
  },
};

export const is_content_label = (id: string) => {
  const label = getInfoType(id);
  return isNotEmpty(label) && label.labelType === "Content_label";
};

export const get_label_parents = (id: string): InformationType[] => {
  const label = allInfoTypes.value[id];
  if (label) {
    // @ts-ignore
    return label.path.map((parent_id) => allInfoTypes.value[parent_id]);
  } else {
    return [];
  }
};
let all_labels: Record<string, InformationType>;
export const getInfoType = (id: string) => {
  if (isEmpty(all_labels)) {
    all_labels = {
      ...allInfoTypes.value,
      ...[
        ...dynamics_labels.value,
        ...virtualLabels.value,
        ...context_labels.value,
        ...folders_context_labels.value,
        ...PII_labels.value,
      ].reduce((acc, t) => {
        // @ts-ignore
        acc[t.id] = t;
        return acc;
      }, {}),
    };
  }
  return all_labels[id];
};

export const isInfoTypeChildOfOne = (payload: {
  child: string;
  parents: string[];
}) => {
  const { child, parents = [] } = payload;
  const childLabel = getInfoType(child);
  if (!childLabel) {
    return false;
  }
  return parents
    .map((p) => {
      const l = getInfoType(p);
      if (l) {
        return l.generation === 0 ? p : l.parentId;
      } else {
        return p;
      }
    })
    .includes(childLabel.parentId || "");
};

export const default_label = getter((s) => {
  return l0InfoTypes.value.find((l) => l.code === 1);
});

export const default_label_id = computed(() => {
  const other_label = default_label.value;
  return other_label ? other_label.id : "?";
});

export const get_info_type_by_code = (code: number) =>
  l0InfoTypes.value.find((l) => l.code === code) as InformationType;

// @ts-ignore
window.getInfoType = getInfoType;

const flat_info_types_tree = flat_by<InformationTypeTree, "nested">(
  "nested",
  (item) => {
    // @ts-ignore
    item.has_children = true;
    return item;
  }
);

/* export const content_labels = {
  get_content_label:(ids:string[])=>{
    const labels = ids.map((id) => getInfoType(id)).filter( d=> d && d.labelType =='Content_label');
    
  }
} */

export const PII = {
  all() {
    return info_types_tree.value[6];
  },
  from_labels(ids: string[]) {
    return ids
      .map((id) => getInfoType(id))
      .filter(
        (d) => d && (d.labelCategory.search("Regulations") > -1 || d.code == 7)
      );
  },
  L1() {
    return PII.all().nested.filter((d) => d.labelCategory == "Regulations");
  },
  L1_content() {
    return PII.all().nested.filter((d) => d.labelCategory == "Content");
  },
  spi(content_code?: number) {
    const all_spi = PII.all().nested.filter((d) => d.id == "spi")![0].nested;
    if (content_code) {
      return all_spi.filter((d) => d.content_code == content_code);
    } else {
      return all_spi;
    }
  },
  ms_labels() {
    const labels = PII.all().nested.filter((d) => d.id == "ms");
    if (labels.length > 0) {
      return labels[0].nested;
    } else {
      return [];
    }
  },
};

const extract_regulated_types_from_tree = (tree: InformationTypeTree[]) => {
  const flat_labels = flat_info_types_tree(clone([...tree], true));
  const regulated_l2s = Object.values(flat_labels).filter(
    (f) => f.regulationType !== "None"
  );
  return [regulated_l2s, uniqby("id")(flat_labels)];
};

const ABSTRACT_REGULATED_L0 = {
  id: "a9383d32-7694-4d84-b37c-79afb945505b",
  labelCategory: "Content",
  labelType: "Content_label",
  labelName: "Personal",
  trustValue: 1,
  riskValue: 0,
  sensitivity: 0,
  concrete: 0,
  threshold: 0,
  code: 7,
  abstracts: [],
  generation: 0,
  infoTypeCategoryId: "a9383d32-7694-4d84-b37c-79afb945505b",
  description: "Regulated info description",
  sensitivityLevel: 0,
  deleted: false,
  regulationType: "None",
  concreteScore: 0,
  path: ["a9383d32-7694-4d84-b37c-79afb945505b"],
  shortName: "Personal",
  name: "Personal Information",
  l0Order: 6,
  policySection: {
    alertReceivers: [],
    policies: {},
  },
  parentId: "L0",
};

const agg_children_sensitivities = child_to_parent_tree_injection(
  "nested",
  (child: any, parent: any) => {
    parent.sensitivities = {
      [parent.sensitivityLevel]: true,
      ...(parent.sensitivities || {}),
      [child.sensitivityLevel]: true,
    };
    if (child.sensitivities) {
      parent.sensitivities = {
        ...parent.sensitivities,
        ...child.sensitivities,
      };
    }
    return parent;
  }
);

const relevant_categories = [
  "Regulations",
  "Content",
  "Contains PII",
  "Contains PFI",
  "Contains PCI",
];

/**
 * The label categories of the three regulated parents.
 *
 * labels.api.ts selects these nodes by `labelCategory` (see the `pfi_pci_pii`
 * filter there), so this is the identity that actually travels with the data.
 */
export const REGULATED_PARENT_CATEGORIES = [
  "Contains PII",
  "Contains PFI",
  "Contains PCI",
];

/**
 * Bug 24987: these three sit under Regulated Information but are gen-0 roots,
 * so every consumer gating on `generation == 1` must admit them separately.
 *
 * They used to be admitted by this hardcoded id list alone. The ids are literal
 * GUIDs, while the API identifies the same nodes by labelCategory — so in any
 * org whose label ids differ from these three literals the parents were dropped
 * from `other_personal_categories`, never reached `Personal.nested`, and were
 * therefore absent from BOTH Information Customization and Automate Labeling.
 * Prefer `is_regulated_parent`, which matches on category first and keeps these
 * ids only as a fallback.
 */
export const PCI_PII_PFI_PARENT_IDS = [
  "d805015e-4b4c-44aa-ab82-f9cbcb98b18c",
  "54190511-be8c-4588-8788-18af8dfc8c06",
  "1294e897-ed2f-4f0d-bbdf-5db400fbc02e",
];

/** a Contains PII / PFI / PCI parent, by category with the legacy ids as backup */
export const is_regulated_parent = (label: {
  id?: string;
  labelCategory?: string;
}) =>
  REGULATED_PARENT_CATEGORIES.includes(label.labelCategory || "") ||
  PCI_PII_PFI_PARENT_IDS.includes(label.id || "");

export const loadInfoTypes = action<{ is_before_date_of_new_piis: boolean }>(
  "load info types",
  async ({ mutate, dispatch }, { is_before_date_of_new_piis }) => {
    all_labels = {};
    const has_mip_asn_pii_res = await get_abstract_has_mip_and_PII_labels({
      is_before_date_of_new_piis,
    });
    if (has_mip_asn_pii_res.success) {
      const [has_mip_label] = has_mip_asn_pii_res.value;
      let PII_labels = has_mip_asn_pii_res.value[1];
      if (!SharedState.FeatureHelper.is_enabled("pii_labeler")) {
        // filter the PII children to contain just the SPI  category
        PII_labels = PII_labels.filter(
          (d: InformationType) => relevant_categories.includes(d.labelCategory) // d.labelCategory == "Content"
        );
      }

      mutate(set_abstract_has_mip_labels, has_mip_label);
      mutate(set_PII_labels, PII_labels);
    }
    const dynamics_labels_res = await get_dynamics_labels();
    if (dynamics_labels_res.success) {
      // source code label
      const TMP_REMOVE_ID = "63a20651-381a-44b2-9bd5-9dfb30ccb0a3";
      mutate(
        set_dynamics_labels,
        dynamics_labels_res.value.filter((f) => f.id !== TMP_REMOVE_ID)
      );
    }

    const [
      adhoc_res,
      context_labels_res,
      res,
      folders_context_labels_res,
    ] = await Promise.all([
      get_adhoc_labels(),
      get_context_labels(),
      get_content_labels_tree(),
      get_folders_context_labels(),
    ]);

    if (context_labels_res.success) {
      mutate(set_context_labels, context_labels_res.value);
    }
    if (folders_context_labels_res.success) {
      mutate(set_folders_context_labels, folders_context_labels_res.value);
    }
    if (res.success && adhoc_res.success) {
      const tree = res.value;

      const [regulated_l2s, flattened_tree] = extract_regulated_types_from_tree(
        tree
      );

      const adhoc_labels = adhoc_res.value.map((d: any) => {
        return {
          ...d,
          path: [ABSTRACT_REGULATED_L0.id, d.id],
        };
      });

      const other_personal_categories = PII_labels.value
        .filter(
          // @ts-ignore
          (f) => f.generation == 1 || is_regulated_parent(f)
        )
        .map((d) => {
          return {
            ...d,
            content_code: 7,
            // @ts-ignore
            nested: (d.nested || []).map((f: InformationType) => {
              return {
                ...f,
                content_code: 7,
              };
            }),
          };
        });
      let spi = PII_labels.value.filter(
        (f: InformationType) =>
          // @ts-ignore
          f.labelType != "Content_abstract" &&
          !other_personal_categories.find((d) => d.id == f.id)
      );

      /**
       * Regulated Information. The content tree tags labels with a
       * regulationType, and `extract_regulated_types_from_tree` has always
       * pulled them out - but its result was discarded, so labels like
       * "Candidate Cover Letters" and "Appointments & Promotions" never joined
       * the Sensitive Personal Information list they belong to, alongside CVs,
       * Job Interviews and Payslips. `spi` was built from PII_labels alone.
       * Bug 24987.
       *
       * Merged in BEFORE the shared .map() below so they get the same code,
       * content_code and path treatment as every other SPI member, and deduped
       * against every sibling list a label could already be in.
       */
      const spi_ids = new Set(spi.map((d: InformationType) => d.id));
      const missing_regulated = ((regulated_l2s || []) as InformationType[])
        .filter(
          (d) =>
            d &&
            d.id &&
            !spi_ids.has(d.id) &&
            // @ts-ignore
            d.labelType != "Content_abstract" &&
            !other_personal_categories.find((o) => o.id == d.id) &&
            !adhoc_labels.find((a: { id: string }) => a.id == d.id)
        );
      spi = [...spi, ...missing_regulated];

      spi = spi
        .map((d) => {
          return {
            ...d,
            code: 7,
            content_code: SharedState.allInfoTypes.value[d.id]
              ? SharedState.allInfoTypes.value[d.id].code
              : d.code,
            // @ts-ignore
            content_path: [
              d.infoTypeCategoryId,
              // @ts-ignore
              d.infoTypeSubCategoryId,
              d.id,
            ].filter((d) => d),
            // @ts-ignore
            // Members that arrive from the abstract endpoint have a 2-element
            // path rooted at Personal, but the ones merged in above come from
            // the content tree with 3 elements rooted at their own L0 - so
            // reading positions out of the source path builds an address that
            // does not exist in the tree and update_info_type_in_tree walks
            // off it. Every SPI member lives at exactly this address. Bug 24987.
            path: [ABSTRACT_REGULATED_L0.id, "spi", d.id],
          };
        })
        .sort(
          (a: InformationType, b: InformationType) =>
            a.generation - b.generation
        );

      set_types_by_categories(
        // @ts-ignore
        Object.values(flattened_tree),
        ABSTRACT_REGULATED_L0.id,
        [
          ...spi,
          ...adhoc_labels,
          ...other_personal_categories.filter(
            (d) => d.labelCategory == "Content"
          ),
        ]
      );

      const final_tree = agg_children_sensitivities([
        ...tree,
        {
          ...ABSTRACT_REGULATED_L0,
          nested: [
            {
              code: 7,
              id: "spi",
              parentId: ABSTRACT_REGULATED_L0.id,
              labelName: "Sensitive Personal Information",
              name: "Sensitive Personal Information",
              fake: true,
              nested: spi,
            },
            {
              code: 7,
              id: "ms",
              fake: true,
              parentId: ABSTRACT_REGULATED_L0.id,
              labelType: "Adhoc",
              labelName: "Microsoft labels",
              name: "Microsoft labels",
              nested: [...adhoc_labels],
            },
            ...other_personal_categories,
          ],
        },
      ]);

      mutate(set_info_types_tree, final_tree);

      await delayBy(200);
      await Promise.all([
        // @ts-ignore
        dispatch(load_info_types_descriptions),
        // @ts-ignore
        dispatch(_loadVirtualLabels),
        // @ts-ignore
        dispatch(load_internal_external_abstract_labels),
      ]);

      // marking deleted custom labels as deleted
      const deleted_custom_labels = await DeletedCustomLabelsSettings.get();
      if (deleted_custom_labels && deleted_custom_labels.length > 0) {
        deleted_custom_labels.forEach((id) => {
          const custom_label = allInfoTypes.value[id]!;
          update_info_type_in_tree({
            ...custom_label,
            // @ts-ignore
            deleted: true,
          });
        });
      }
    }
  }
);

export const delete_custom_label = async (label: InformationType) => {
  const current_deleted = await DeletedCustomLabelsSettings.get();
  if (current_deleted && !current_deleted.includes(label.id)) {
    await DeletedCustomLabelsSettings.update([...current_deleted, label.id]);
    update_info_type_in_tree({
      ...label,
      // @ts-ignore
      deleted: true,
    });
  }
};

export const filter_out_deleted_labels = (items: { id: string }[]) => {
  // @ts-ignore
  return items.filter(({ id }) => !allInfoTypes.value[id].deleted);
};

export const update_info_types_sensitivity = action<
  Array<{ id: string; sensitivity: number }>
>("update info types sensitivity", async ({ mutate }) => {
  // @ts-ignore
  const res = await get_content_labels_tree();
  if (res.success) {
    const tree = res.value;
    mutate(set_info_types_tree, tree);
  }
});

export const load_info_types_descriptions = action<void>(
  "load info type descriptions",
  async ({ mutate }) => {
    const resForL0Descs = await objectsSummary({
      ids: [...l0InfoTypes.value.map((l) => l.id)],
      type: POPULATION_TYPE.LABEL,
    });
    let all_descs: Record<string, string> = {};
    if (resForL0Descs.success) {
      const l0Descs = resForL0Descs.value.reduce((acc, item) => {
        // @ts-ignore
        acc[item.id] = item.description;
        return acc;
      }, {} as { [key: string]: string });

      all_descs = {
        ...l0Descs,
      };
    }
    const descResult = await getL1sDesc();
    if (descResult.success) {
      all_descs = {
        ...all_descs,
        ...descResult.value,
      };
      mutate(set_info_types_desc, all_descs);
    }
  }
);

export const update_custom_labels_text = (label: InformationTypeTree) => {
  const tree = get_raw_info_types_tree();
  const L0 = tree.find((f) => f.id === label.parentId);
  if (L0) {
    L0.nested = L0.nested.map((l) => {
      if (l.id === label.id) {
        return {
          ...label,
        };
      }
      return l;
    });
    set_info_types_tree(tree);
  }
};

export const add_custom_content_label = (the_new_label: any) => {
  const tree = get_raw_info_types_tree();
  const L0 = tree.find((f) => f.code === the_new_label.code);
  if (L0) {
    L0.nested = [
      {
        ...the_new_label,
        labelCategory: "Content",
        labelType: "Content_label",
        path: [the_new_label.parentId, the_new_label.id],
        infoTypeSubCategoryId: the_new_label.parentId,
        abstracts: [the_new_label.parentId],
        policySection: {
          alertReceivers: [],
          policies: {},
        },
        nested: [],
        concreteScore: 1,
        labelName: the_new_label.name,
        shortName: the_new_label.name.split(" ").join("__"),
        deleted: false,
        origin: "Manual",
        generation: 1,
        sensitivityTypeName: the_new_label.sensitivityType,
        // @ts-ignore
        trustValue: 1,
        riskValue: 0,
        // @ts-ignore
        concrete: 1,
        threshold: 0,
        sensitivity: SensitivityLevelHelper.intToFloat(
          the_new_label.sensitivity
        ),
        sensitivityLevel: the_new_label.sensitivity,
      },
      ...L0.nested,
    ];
    set_info_types_tree(tree);
  }
};
